// White paper landing pages and the shared lead rules. Plain JS so the
// Vercel function in api/lead.js and the node tests can import it too.

export const LEAD_ENDPOINT = '/api/lead';
export const LEAD_STORAGE_KEY = 'gfd_white_paper_email';

export const WHITE_PAPERS = [
  {
    slug: 'ecommerce-data-stack',
    path: '/white-papers/ecommerce-data-stack',
    pdf: '/downloads/ecommerce-data-stack-white-paper.pdf',
    cover: '/images/downloads/ecommerce-data-stack-white-paper-cover.webp',
    coverAlt: 'Cover of the white paper The Ecommerce and Consumer Brand Data Stack, with the seven layers of the stack',
    title: 'The Ecommerce and Consumer Brand Data Stack',
    shortTitle: 'Ecommerce data stack white paper',
    pages: 18,
    size: '2.1 MB',
    seoTitle: 'Free white paper: The Ecommerce and Consumer Brand Data Stack',
    seoDescription:
      'An 18-page white paper for DTC and omnichannel consumer brands: which data tools to choose at each stage, and a five-phase implementation plan with owners and acceptance checks.',
    lede: 'Tool selection by stage, and a phased implementation plan. Written for founders, finance leads, growth leads, and the first data hire at DTC and omnichannel consumer brands.',
    inside: [
      {
        heading: 'Choose by stage, not by logo',
        body: 'A recommended stack for a single-channel Shopify brand, a multi-channel brand on Amazon and TikTok Shop, and an omnichannel brand with wholesale and retail.',
      },
      {
        heading: 'Packaged tool or warehouse',
        body: 'When Triple Whale, Polar, Peel, Daasity, and others are the right call, and the signals that you have outgrown one.',
      },
      {
        heading: 'Definitions before tools',
        body: 'The seven layers of the stack, and why a written metric dictionary is the cheapest layer and the one everything else reads.',
      },
      {
        heading: 'A five-phase plan',
        body: 'Deliverables, owners, acceptance checks, and risks for each phase, plus a readiness checklist and common failure modes.',
      },
    ],
    contents: [
      ['Who this paper is for', 3],
      ['The seven layers of the stack', 4],
      ['The ecommerce stack, mapped', 5],
      ['Packaged analytics or a warehouse', 6],
      ['Stage 1: single-channel Shopify', 7],
      ['Stage 2: multi-channel', 8],
      ['Stage 3: omnichannel', 9],
      ['Ingestion', 10],
      ['Modeling, semantic layer, BI, activation', 11],
      ['The implementation plan, phases 0 to 4', 12],
      ['Where to start: readiness checklist', 16],
      ['Common failure modes', 17],
    ],
    next: {
      text: 'Next, apply the framework to your brand with the',
      href: '/tools/ecommerce-data-stack-quiz',
      label: 'ecommerce data stack quiz',
    },
    related: 'customer-retention-reporting',
  },
  {
    slug: 'customer-retention-reporting',
    path: '/white-papers/customer-retention-reporting',
    pdf: '/downloads/customer-retention-reporting-white-paper.pdf',
    cover: '/images/downloads/customer-retention-reporting-white-paper-cover.webp',
    coverAlt: 'Cover of the white paper Customer Retention Reporting for Consumer Brands, with a screenshot of the 90-Day Retention report',
    title: 'Customer Retention Reporting for Consumer Brands',
    shortTitle: 'Retention reporting white paper',
    pages: 13,
    size: '1.7 MB',
    seoTitle: 'Free white paper: Customer Retention Reporting for Consumer Brands',
    seoDescription:
      'A 13-page white paper on retention reporting for consumer brands: five questions, the metric and "who is included" rule behind each, a metric dictionary, and a mistakes checklist.',
    lede: 'Five questions, the metrics behind them, and how to build them. Based on the Consumer Brand Retention Starter Pack, a public Sigma workbook. Every figure in the paper uses synthetic data for Willow, a made-up menswear brand.',
    inside: [
      {
        heading: 'Write down who is included',
        body: 'Why retention numbers disagree between teams, and the eligibility rule that stops the newest cohort from always looking worst.',
      },
      {
        heading: 'One question per report',
        body: 'Customer Activity, 90-Day Retention, 24-Month Frequency, Product Mix, and Reactivation, each with its metrics and what to look for.',
      },
      {
        heading: 'A metric dictionary and data model',
        body: 'Every formula with its "who is included" rule, the data you need, and how to model it in three layers.',
      },
      {
        heading: 'A dollar value on retention',
        body: 'A short formula that turns a change in repeat rate into revenue and gross profit, plus a common mistakes checklist.',
      },
    ],
    contents: [
      ['Why retention numbers disagree', 3],
      ['The five reports', 4],
      ['Metric dictionary', 9],
      ['Data you need and how to model it', 10],
      ['Common mistakes checklist', 11],
      ['Putting a dollar value on retention', 12],
    ],
    next: {
      text: 'Next, explore the five reports in the',
      href: '/blog/consumer-brand-retention-dashboard-in-sigma',
      label: 'retention dashboard walkthrough',
    },
    related: 'ecommerce-data-stack',
  },
];

export function getWhitePaper(slug) {
  return WHITE_PAPERS.find((paper) => paper.slug === slug);
}

const EMAIL_PATTERN = /^[^\s@<>()[\]\\,;:"]+@[^\s@<>()[\]\\,;:"]+\.[a-z]{2,}$/i;

export function isValidEmail(value) {
  if (typeof value !== 'string') return false;
  const email = value.trim();
  return email.length > 0 && email.length <= 254 && EMAIL_PATTERN.test(email) && !email.includes('..');
}

export const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];

export function utmFromSearch(search) {
  const params = new URLSearchParams(search || '');
  const utm = {};
  for (const key of UTM_KEYS) {
    const value = params.get(key);
    if (value) utm[key] = value.slice(0, 200);
  }
  return utm;
}
